import { useQueries } from "@tanstack/react-query";
import { merge, uniqBy, zipObjectDeep } from "lodash";
import type { OptionPriceValue, ProductPricesResponse } from "../../admin/products/productsApi";
import { getProductSellingPrices } from "../../admin/products/productsApi";
import { productSellingPricesQueryKey } from "../../admin/products/useProductSellingPrices";

export type PricesByDatesProductsAndOptions = Record<
  string,
  Record<string, Record<string, { readonly price: OptionPriceValue; readonly availableQuantity?: number }>>
>;

interface Product {
  readonly id: string;
  readonly dateFrom: string;
  readonly dateTo: string;
}

export const useProductsSellingPrices = (
  products: Product[],
  participantsNumber: number,
): { isLoading: boolean; prices: PricesByDatesProductsAndOptions } => {
  const uniqueProducts = uniqBy(products, (p) => `${p.id}:${p.dateFrom}:${p.dateTo}:${participantsNumber}`);

  const priceQueries = useQueries({
    queries: uniqueProducts.map(({ id, dateFrom, dateTo }) => ({
      queryKey: productSellingPricesQueryKey(id, { dateFrom, dateTo, participantsNumber }),
      queryFn: ({ signal }) => getProductSellingPrices(id, { dateFrom, dateTo, participantsNumber }, signal),
    })),
  });
  const queriesData = priceQueries.map((q) => q.data);
  const prices = queriesData.reduce(
    (prev, curr, idx) => reducePrices(prev, curr, products[idx]),
    {} as PricesByDatesProductsAndOptions,
  );
  return {
    isLoading: priceQueries.some((q) => q.isLoading),
    prices,
  };
};

function reducePrices(
  prev: PricesByDatesProductsAndOptions,
  priceResponse: ProductPricesResponse | undefined,
  product: Product,
): PricesByDatesProductsAndOptions {
  if (!priceResponse) {
    return prev;
  }
  const entries = Object.entries(priceResponse).flatMap(([optionId, optionData]) =>
    Object.entries(optionData).map(([date, price]) => [date, optionId, price]),
  );
  const patch = zipObjectDeep(
    entries.map(([date, optionId]) => `${date}.${product.id}.${optionId}`),
    entries.map(([, , price]) => price),
  );

  return merge(prev, patch);
}
